import { useData } from "../../runtime/provider";
import type { DataSession } from "../../data/core/session";
import { useMutation } from "@tanstack/react-query";
import { RouterInputs } from "@utils/trpc";
import { captureException } from "@utils/captureException";
import { gamificationSummaryQueryKey } from "./useGamificationSummary";

type Input = RouterInputs["accesses"]["createMusicUnlock"];

/** A refusal the fan is told about (not on sale, a code that does not
 *  work), not a failure to report as one. */
const isMusicUnlockRefusal = (error: unknown) => {
  const code = (error as { data?: { code?: unknown } })?.data?.code;
  return code === "BAD_REQUEST" || code === "NOT_FOUND";
};

/** The Purchase Gems an unlock earns are the server's: read them again. */
export const refreshGemsAfterUnlock = (data: DataSession) => {
  data.queryClient
    .invalidateQueries({ queryKey: gamificationSummaryQueryKey() })
    .catch((error) =>
      captureException(error, {
        channel: "breadcrumb",
        area: "checkout",
        operation: "invalidate-gamification-after-music-unlock",
      }),
    );
};

/**
 * Unlocks music through the ticketing engine (LIS-1038): unlocked outright
 * when it is free (or the unlock code is valid), owned already, or an
 * engine checkout held for its price.
 */
const useAccessCreateMusicUnlock = () => {
  const data = useData();
  return useMutation({
    mutationFn: (input: Input) =>
      data.api.accesses.createMusicUnlock.mutate(input),
    onSuccess: (result) => {
      if (result.kind !== "engine") refreshGemsAfterUnlock(data);
    },
    onError: (error, input) =>
      captureException(error, {
        channel: isMusicUnlockRefusal(error) ? "breadcrumb" : "error",
        area: "checkout",
        operation: "create-music-unlock",
        tags: { accessId: input.accessId },
      }),
  });
};

export default useAccessCreateMusicUnlock;
